import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  AdminHeaderAction,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  SEARCH_MATCHING_ROUTE,
  buildSampleSearchMatchingRules,
  formatDefaultValue,
  formatSearchMatchingDateTime,
  formatSelectedValues,
  getRuleAreaLabel,
} from '../../../data/searchMatchingConfig';

const tableCols =
  'minmax(72px, 0.55fr) minmax(120px, 1fr) minmax(110px, 0.95fr) minmax(88px, 0.7fr) minmax(160px, 1.4fr) minmax(110px, 0.9fr) minmax(110px, 0.85fr) minmax(90px, 0.7fr) minmax(56px, 0.22fr)';

const TABLE_MIN_WIDTH = '1180px';

const SearchMatching = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(() => buildSampleSearchMatchingRules());
  const [feedback, setFeedback] = useState('');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const displayedRules = useMemo(
    () =>
      [...rules].sort((a, b) => {
        const tb = new Date(b.lastUpdatedOn || b.updatedOn || 0).getTime();
        const ta = new Date(a.lastUpdatedOn || a.updatedOn || 0).getTime();
        if (tb !== ta) return tb - ta;
        return String(a.id || '').localeCompare(String(b.id || ''));
      }),
    [rules],
  );

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(displayedRules);

  useEffect(() => {
    const incoming = location.state?.rules;
    const message = location.state?.feedback;
    if (Array.isArray(incoming) || message) {
      if (Array.isArray(incoming)) setRules(incoming);
      if (message) setFeedback(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openAdd = () => {
    navigate(`${SEARCH_MATCHING_ROUTE}/add`, { state: { existingRules: rules } });
  };

  const openRuleView = (rule) => {
    navigate(`${SEARCH_MATCHING_ROUTE}/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, viewMode: true, existingRules: rules },
    });
  };

  const openRuleEdit = (rule) => {
    navigate(`${SEARCH_MATCHING_ROUTE}/${urlNameSegment(rule.ruleName)}/edit`, {
      state: { rule, existingRules: rules },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Search & Matching</AdminPageTitle>
          <AdminHeaderAction onClick={openAdd}>Add New</AdminHeaderAction>
        </div>

        {feedback ? (
          <AdminSupportingText className="text-freshgreen mb-4">{feedback}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {displayedRules.length === 0 ? (
              <div className="py-10 px-4 text-center space-y-4">
                <AdminPanelTitle className="text-offblack">No Search & Matching rules found</AdminPanelTitle>
                <div className="flex justify-center pt-2">
                  <AdminHeaderAction onClick={openAdd}>Add New</AdminHeaderAction>
                </div>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  {[
                    'Rule ID',
                    'Rule Name',
                    'Rule Area',
                    'Conditions',
                    'Selected Values',
                    'Default Value',
                    'Last Updated',
                    'Last Updated By',
                    'Actions',
                  ].map((header) => (
                    <div
                      key={header}
                      className={`flex items-center min-w-0 ${
                        header === 'Actions' ? 'justify-center shrink-0' : ''
                      }`}
                    >
                      <AdminTableHeader className="truncate">{header}</AdminTableHeader>
                    </div>
                  ))}
                </div>

                {paginatedRules.map((rule) => {
                  const selectedValues = formatSelectedValues(rule);
                  const defaultValue = formatDefaultValue(rule);
                  const areaLabel = getRuleAreaLabel(rule.ruleArea);

                  return (
                    <div
                      key={rule.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openRuleView(rule)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRuleView(rule);
                        }
                      }}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                      style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                    >
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block" title={rule.id}>
                          {rule.id}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={rule.ruleName}>
                          {rule.ruleName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={areaLabel}>
                          {areaLabel}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">No Conditions</AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="block break-words whitespace-normal" title={selectedValues}>
                          {selectedValues}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="block break-words whitespace-normal" title={defaultValue}>
                          {defaultValue}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">
                          {formatSearchMatchingDateTime(rule.lastUpdatedOn || rule.updatedOn)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">
                          {rule.lastUpdatedBy || rule.updatedBy || '—'}
                        </AdminTableBody>
                      </div>
                      <div
                        className="flex items-center justify-center shrink-0 overflow-visible"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[rule.id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(rule.id);
                            }}
                            aria-expanded={!!rowActionsOpen[rule.id]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[rule.id] && rowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openRuleView(rule);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openRuleEdit(rule);
                                }}
                              >
                                Edit
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default SearchMatching;
